/** What a recognized sensitive value is replaced with. */
export const SENSITIVE_DATA_PLACEHOLDER = "[redacted]";

/** Card numbers as people write them: 4-4-4-4 (optionally 1 to 3 more), Amex 4-6-5, or 13 to 19 digits in a row. */
const CARD_CANDIDATE =
  /(?<![\d-])(?:\d{4}([ -])\d{4}\1\d{4}\1\d{4}(?:\1\d{1,3})?|\d{4}([ -])\d{6}\2\d{5}|\d{13,19})(?![\d-])/g;
/** Length of a 4-4-4-4 card number with its three separators. */
const GROUPED_CARD_LENGTH = 19;
/** IBANs: country, check digits, then the account part in one run or in groups of four. */
const IBAN_CANDIDATE = /\b[A-Z]{2}\d{2}(?:[ -]?[A-Z0-9]{4}){2,7}(?:[ -]?[A-Z0-9]{1,3})?\b/g;
/** Key formats that are secrets on sight; each needs enough characters that ordinary words do not match. */
const KEY_PATTERNS = [
  /(?<![A-Za-z0-9_-])sk-(?:proj-|ant-|or-v1-)?[A-Za-z0-9_-]{20,}/g,
  /\bgithub_pat_[A-Za-z0-9_]{22,}/g,
  /\bgh[pousr]_[A-Za-z0-9]{36,}\b/g,
  /\bxox[abprs]-[A-Za-z0-9-]{10,}/g,
  /\bAKIA[0-9A-Z]{16}\b/g,
  /\bAIza[0-9A-Za-z_-]{35}(?![0-9A-Za-z_-])/g,
  /\bglpat-[A-Za-z0-9_-]{20,}/g,
  /\b(?:sk|rk)_(?:live|test)_[A-Za-z0-9]{16,}\b/g,
];
/**
 * A password named in German or English, then the value: "Passwort: x", "das Kennwort lautet x", "password is x".
 * The short word pw counts only before ":" or "=", and PIN only in capitals, so "GPIO pin 3" stays.
 */
const PASSWORD_PHRASE =
  /\b(passwort|kennwort|password|passwd|passphrase|pin-code|pincode|geheimzahl|zugangscode|pw|pin)(\s*(?::|=|\blautet\b:?|\bist\b:?|\bis\b:?|\bwas\b:?)\s*)("[^"\n]{1,200}"|'[^'\n]{1,200}'|„[^“\n]{1,200}“|[^\s,;]{1,200})/gi;

function luhnValid(digits: string): boolean {
  let sum = 0;
  for (let index = 0; index < digits.length; index++) {
    let digit = Number(digits[digits.length - 1 - index]);
    if (index % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

function ibanValid(candidate: string): boolean {
  const iban = candidate.replace(/[ -]/g, "");
  if (iban.length < 15 || iban.length > 34) return false;
  let remainder = 0;
  for (const char of `${iban.slice(4)}${iban.slice(0, 4)}`) {
    const value = /\d/.test(char) ? char : String(char.charCodeAt(0) - 55);
    for (const digit of value) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

/**
 * The text with card numbers, IBANs, API keys and stated passwords replaced by a placeholder, and how many were
 * replaced. Cards and IBANs count only with a valid checksum, so order numbers and dates stay.
 */
export function redactSensitiveData(text: string): { text: string; count: number } {
  let count = 0;
  const replace = () => {
    count += 1;
    return SENSITIVE_DATA_PLACEHOLDER;
  };
  let result = text.replace(PASSWORD_PHRASE, (match, keyword: string, separator: string) => {
    if (keyword.toLowerCase() === "pw" && !/[:=]/.test(separator)) return match;
    if (keyword.toLowerCase() === "pin" && keyword !== "PIN") return match;
    return `${keyword}${separator}${replace()}`;
  });
  for (const pattern of KEY_PATTERNS) result = result.replace(pattern, replace);
  result = result.replace(IBAN_CANDIDATE, (match) => (ibanValid(match) ? replace() : match));
  result = result.replace(CARD_CANDIDATE, (match) => {
    if (luhnValid(match.replace(/[ -]/g, ""))) return replace();
    // "4111 1111 1111 1111 12/27": the expiry month was read as a fifth group.
    const card = match.slice(0, GROUPED_CARD_LENGTH);
    if (match.length > GROUPED_CARD_LENGTH && luhnValid(card.replace(/[ -]/g, ""))) {
      return `${replace()}${match.slice(GROUPED_CARD_LENGTH)}`;
    }
    return match;
  });
  return { text: result, count };
}
